import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentDateVN } from "./utils/dailyUsageManager";
import {
  getDailyStats,
  getTopUsers,
  getUsageDistribution,
  getHourlyBreakdown,
  exportAnalyticsData,
} from "./utils/usageTracking";
import "./AdminUserManagement.css";

const AdminUsageAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getCurrentDateVN());

  // Stats state
  const [dailyStats, setDailyStats] = useState({
    totalRequests: 0,
    uniqueUsers: 0,
    limitReachedCount: 0,
    averageUsagePerUser: 0,
  });

  const [topUsers, setTopUsers] = useState([]);
  const [distribution, setDistribution] = useState({
    zero: 0,
    one: 0,
    two: 0,
    three: 0,
    fourToTwenty: 0,
    moreThanTwenty: 0,
  });
  const [hourlyData, setHourlyData] = useState(Array(24).fill(0));

  // Export state
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Toast
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load all analytics data
        const [stats, users, dist, hourly] = await Promise.all([
          getDailyStats(selectedDate),
          getTopUsers(selectedDate, 10),
          getUsageDistribution(selectedDate),
          getHourlyBreakdown(selectedDate),
        ]);

        setDailyStats(stats);
        setTopUsers(users);
        setDistribution(dist);
        setHourlyData(hourly);

        setLoading(false);
      } catch (error) {
        console.error("Error loading analytics:", error);
        showToast("Lỗi khi tải dữ liệu", "error");
        setLoading(false);
      }
    };

    loadData();
  }, [selectedDate]);

  // Handle export
  const handleExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      showToast("Vui lòng chọn khoảng thời gian", "error");
      return;
    }

    try {
      setIsExporting(true);
      const csv = await exportAnalyticsData(exportStartDate, exportEndDate);

      // Download CSV
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `usage-analytics-${exportStartDate}-to-${exportEndDate}.csv`;
      link.click();

      showToast("Xuất dữ liệu thành công!");
      setIsExporting(false);
    } catch (error) {
      console.error("Error exporting:", error);
      showToast("Lỗi khi xuất dữ liệu", "error");
      setIsExporting(false);
    }
  };

  // Get max value for chart scaling
  const maxHourlyValue = Math.max(...hourlyData, 1);

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              <h3>Đang tải dữ liệu...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Toast */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="content-header">
        <div>
          <h2>Phân tích sử dụng</h2>
          <p>Dashboard thống kê và analytics</p>
        </div>
        <button
          onClick={() => navigate("/admin-dashboard")}
          className="btn btn-primary"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Quay lại Dashboard
        </button>
      </div>

      {/* Date selector */}
      <div className="data-table" style={{ marginBottom: "24px" }}>
        <div className="table-content" style={{ padding: "16px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <label style={{ fontWeight: "500" }}>Chọn ngày:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form-input"
              style={{ width: "200px" }}
            />
            <button
              onClick={() => setSelectedDate(getCurrentDateVN())}
              className="btn btn-secondary"
            >
              Hôm nay
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: "24px" }}>
        <div className="stat-card users-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Tổng requests</p>
              <p className="stat-card-value">{dailyStats.totalRequests}</p>
            </div>
          </div>
        </div>

        <div className="stat-card active-users-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Unique Users</p>
              <p className="stat-card-value">{dailyStats.uniqueUsers}</p>
            </div>
          </div>
        </div>

        <div className="stat-card transactions-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Đạt giới hạn</p>
              <p className="stat-card-value">{dailyStats.limitReachedCount}</p>
            </div>
          </div>
        </div>

        <div className="stat-card revenue-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">TB/User</p>
              <p className="stat-card-value">
                {dailyStats.averageUsagePerUser.toFixed(1)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hourly Breakdown Chart */}
      <div className="data-table" style={{ marginBottom: "24px" }}>
        <div className="table-header">
          <div className="table-title">
            <h3>Phân bố theo giờ (ngày {selectedDate})</h3>
          </div>
        </div>
        <div className="table-content" style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "4px",
              height: "200px",
            }}
          >
            {hourlyData.map((count, hour) => (
              <div
                key={hour}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#0ea5e9",
                    borderRadius: "4px 4px 0 0",
                    height: `${(count / maxHourlyValue) * 160}px`,
                    minHeight: count > 0 ? "4px" : "0",
                    position: "relative",
                  }}
                  title={`${hour}h: ${count} requests`}
                >
                  {count > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontSize: "10px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      {count}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#666",
                    marginTop: "4px",
                    transform: "rotate(-45deg)",
                    transformOrigin: "top left",
                  }}
                >
                  {hour}h
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Usage Distribution */}
      <div className="data-table" style={{ marginBottom: "24px" }}>
        <div className="table-header">
          <div className="table-title">
            <h3>Phân bố users theo số lượt sử dụng</h3>
          </div>
        </div>
        <div className="table-content" style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "space-around",
            }}
          >
            {[
              { label: "0 lượt", value: distribution.zero, color: "#e0e0e0" },
              { label: "1 lượt", value: distribution.one, color: "#a5d6a7" },
              { label: "2 lượt", value: distribution.two, color: "#66bb6a" },
              {
                label: "3-10 lượt",
                value: distribution.three,
                color: "#43a047",
              },
              {
                label: "11-20 lượt",
                value: distribution.fourToTwenty,
                color: "#388e3c",
              },
              {
                label: ">20 lượt",
                value: distribution.moreThanTwenty,
                color: "#2e7d32",
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  textAlign: "center",
                  flex: 1,
                  padding: "16px",
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: item.color,
                    margin: "0 auto 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {item.value}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Users */}
      <div className="data-table" style={{ marginBottom: "24px" }}>
        <div className="table-header">
          <div className="table-title">
            <h3>Top 10 Users sử dụng nhiều nhất</h3>
          </div>
        </div>
        <div className="table-content">
          {topUsers.length === 0 ? (
            <div className="empty-state" style={{ padding: "40px" }}>
              <p>Chưa có dữ liệu</p>
            </div>
          ) : (
            <table className="modern-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Số lượt</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((user, index) => (
                  <tr key={user.userId}>
                    <td>
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor:
                            index === 0
                              ? "#ffd700"
                              : index === 1
                              ? "#c0c0c0"
                              : index === 2
                              ? "#cd7f32"
                              : "#e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          color: index < 3 ? "white" : "#666",
                        }}
                      >
                        {index + 1}
                      </div>
                    </td>
                    <td>
                      <div className="user-name">{user.displayName}</div>
                    </td>
                    <td className="user-email">{user.email}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor: "#e3f2fd",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontWeight: "500",
                        }}
                      >
                        {user.count} lượt
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Export Section */}
      <div className="data-table">
        <div className="table-header">
          <div className="table-title">
            <h3>Xuất dữ liệu</h3>
          </div>
        </div>
        <div className="table-content" style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <label>Từ ngày:</label>
            <input
              type="date"
              value={exportStartDate}
              onChange={(e) => setExportStartDate(e.target.value)}
              className="form-input"
              style={{ width: "180px" }}
            />
            <label>Đến ngày:</label>
            <input
              type="date"
              value={exportEndDate}
              onChange={(e) => setExportEndDate(e.target.value)}
              className="form-input"
              style={{ width: "180px" }}
            />
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="btn btn-success"
            >
              {isExporting ? "Đang xuất..." : "Xuất CSV"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsageAnalytics;
